import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Box, Button, Stack, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import React from 'react'
import { i18n } from '../../index/index'
import { useValueFormat } from './valueFormat'

export interface AllocationBarProps {
  yearlyTarget: number
  allocated: number
  /** Show the "Jakamatta / Jaettu" status. Off when there are no monthly targets to allocate. */
  showAllocation?: boolean
  isFormValid: boolean
  onCancel: () => void
  onSave: () => void
}

/**
 * Edit-mode bottom bar: left side shows the allocation status
 * ("Kaikki täsmää" when balanced, otherwise "Jakamatta X" + "Jaettu Y / Z"), right side
 * holds Peruuta / Tallenna. Only rendered while editing; view mode uses the header MUOKKAA.
 */
export function AllocationBar({
  yearlyTarget,
  allocated,
  showAllocation = true,
  isFormValid,
  onCancel,
  onSave,
}: AllocationBarProps) {
  const { format } = useValueFormat()
  const unallocated = Math.round(yearlyTarget - allocated)
  const balanced = unallocated === 0

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent='space-between'
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        py: 1.5,
        mt: 2,
        boxShadow: (theme) => `0 -2px 8px ${theme.palette.action.hover}`,
      }}
    >
      <Box>
        {showAllocation && (
          <>
            <Stack direction='row' spacing={1} alignItems='center'>
              {balanced ? (
                <CheckCircleOutlineIcon fontSize='small' color='success' />
              ) : (
                <ErrorOutlineIcon fontSize='small' color='warning' />
              )}
              <Typography variant='body2' color={balanced ? 'success.main' : 'warning.main'}>
                {balanced
                  ? i18n.t('admin.monthlyTargets.allBalanced')
                  : i18n.t('admin.monthlyTargets.unallocated', { amount: format(unallocated) })}
              </Typography>
            </Stack>
            <Typography variant='caption' color='text.secondary'>
              {i18n.t('admin.monthlyTargets.allocatedOfTotal', {
                allocated: format(allocated),
                total: format(yearlyTarget),
              })}
            </Typography>
          </>
        )}
      </Box>

      <Grid container direction='row' spacing={2} justifyContent='flex-end'>
        <Button type='submit' disabled={!isFormValid} variant='contained' color='primary' onClick={onSave}>
          {i18n.t('buttons.save')}
        </Button>
        <Button type='button' variant='outlined' color='primary' onClick={onCancel}>
          {i18n.t('buttons.cancel')}
        </Button>
      </Grid>
    </Stack>
  )
}
